import { computed, onUnmounted, ref, ShallowRef, watch } from 'vue'
import { useDayjs } from '@/composables/useDayjs'

export const useAudio = (
  audioElement: Readonly<ShallowRef<HTMLAudioElement | null>>,
  playingByDefault: boolean = false
) => {
  const { formatTime } = useDayjs()

  const currentTime = ref<number>()
  const currentTimeFormatted = computed(() => formatTime(currentTime.value))
  const totalTime = ref<number>()
  const totalTimeFormatted = computed(() => formatTime(totalTime.value))
  const percentPlayed = computed(() => {
    if (typeof currentTime.value === 'undefined' || typeof totalTime.value === 'undefined' || totalTime.value === 0) {
      return 0
    }

    return (currentTime.value / totalTime.value) * 100
  })
  const isPlaying = ref<boolean>(false)
  const isMuted = ref(false)

  const onTimeUpdate = () => {
    const currentTimeSeconds = audioElement.value?.currentTime
    if (typeof currentTimeSeconds === 'number') {
      currentTime.value = currentTimeSeconds * 1000
    }
  }

  const onDurationChange = () => {
    const durationSeconds = audioElement.value?.duration
    if (typeof durationSeconds === 'number' && !isNaN(durationSeconds)) {
      totalTime.value = durationSeconds * 1000
    }
  }

  const updateIsPlaying = () => {
    if (audioElement.value !== null) {
      isPlaying.value = !audioElement.value.paused
    }
  }

  const onLoadeddata = () => {
    currentTime.value = 0
  }

  const reset = () => {
    isPlaying.value = false
    isMuted.value = false
    currentTime.value = undefined
    totalTime.value = undefined
  }

  const destroy = () => {
    audioElement.value?.removeEventListener('timeupdate', onTimeUpdate)
    audioElement.value?.removeEventListener('durationchange', onDurationChange)
    audioElement.value?.removeEventListener('playing', updateIsPlaying)
    audioElement.value?.removeEventListener('pause', updateIsPlaying)
    audioElement.value?.removeEventListener('loadeddata', onLoadeddata)
  }

  watch(audioElement, () => {
    destroy()
    reset()
    audioElement.value?.addEventListener('timeupdate', onTimeUpdate)
    audioElement.value?.addEventListener('durationchange', onDurationChange)
    audioElement.value?.addEventListener('playing', updateIsPlaying)
    audioElement.value?.addEventListener('pause', updateIsPlaying)
    audioElement.value?.addEventListener('loadeddata', onLoadeddata)

    if (playingByDefault) {
      audioElement.value?.play()
    }
  })

  onUnmounted(destroy)

  const togglePlayStop = () => {
    if (audioElement.value === null) {
      return
    }

    if (audioElement.value.paused) {
      audioElement.value.play()
    } else {
      audioElement.value.pause()
    }
  }

  const toggleMute = () => {
    if (audioElement.value === null) {
      return
    }

    if (isMuted.value) {
      audioElement.value.volume = 1
    } else {
      audioElement.value.volume = 0
    }

    isMuted.value = !isMuted.value
  }

  const changePlaybackPosition = (percent: number) => {
    if (audioElement.value !== null && typeof totalTime.value !== 'undefined') {
      audioElement.value.currentTime = (totalTime.value * percent) / 100 / 1000
    }
  }

  const forward = (seconds: number) => {
    if (audioElement.value !== null && typeof totalTime.value !== 'undefined') {
      audioElement.value.currentTime += seconds
    }
  }

  const backward = (seconds: number) => {
    if (audioElement.value !== null && typeof totalTime.value !== 'undefined') {
      audioElement.value.currentTime -= seconds
    }
  }

  return {
    currentTime,
    currentTimeFormatted,
    totalTime,
    totalTimeFormatted,
    isPlaying,
    isMuted,
    percentPlayed,
    toggleMute,
    togglePlayStop,
    changePlaybackPosition,
    backward,
    forward
  }
}
